import {
  AfterViewChecked,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { UserTeamGraph } from 'src/app/models/user-team-graph.model';
import { GraphService } from 'src/app/services/graph/graph.service';
import { Error } from 'src/app/models/error.model';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { MatPaginator } from '@angular/material/paginator';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { ToolBoxService } from 'src/app/services/toolbox/toolbox.service';

@Component({
  selector: 'app-teams-table',
  templateUrl: './teams-table.component.html',
  styleUrl: './teams-table.component.scss',
  standalone: false,
})
export class TeamsTableComponent
  implements OnInit, AfterViewChecked, OnChanges
{
  isBusy: boolean;
  microsoftTeamsUser: UserTeamGraph[];
  defaultColumns: string[] = [
    'photo',
    'displayName',
    'isOwner',
    'isArchived',
    'view',
  ];
  displayedColumnsTeams: string[] = [...this.defaultColumns];
  hiddenColumns: string[] = [];
  expandedUserTeam: UserTeamGraph | null;
  dataSourceTeams: MatTableDataSource<UserTeamGraph>;
  teamsNameFilter: FormControl = new FormControl('');
  teamsUserTypeFilter: FormControl = new FormControl('');
  teamsUserOwnerFilter: FormControl = new FormControl('');
  teamsUserMemberFilter: FormControl = new FormControl('');
  teamsStatusFilter: FormControl = new FormControl('');
  teamsStatusOnlineFilter: FormControl = new FormControl('');
  teamsStatusArchivedFilter: FormControl = new FormControl('');
  filterValuesTeams = {
    displayName: '',
    id: '',
    userType: '',
    status: '',
  };

  clickRow = false;
  innerWidth: number;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };
  @Input() userId: string;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private _graphSerivce: GraphService,
    private _router: Router,
    private _localStorageService: LocalStorageService,
    private _toolboxService: ToolBoxService
  ) {}

  ngAfterViewChecked() {
    if (!this.dataSourceTeams.sort || !this.dataSourceTeams.paginator) {
      this.dataSourceTeams.sort = this.sort;
      this.dataSourceTeams.paginator = this.paginator;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['userId'] && changes['userId'].currentValue) {
      this.getMicrosoftUserTeams(this.userId);
    }
  }

  ngOnInit() {
    this.setVisibleColumns();
    this.initTeamsFilter();
    this.dataSourceTeams = new MatTableDataSource();
    if (this.microsoftTeamsUser) {
      this.initializeDataSourceTeams();
    } else if (this.userId && !this.isBusy) {
      this.getMicrosoftUserTeams(this.userId);
    }
  }

  redirectToTeam(teamId: string, fromRow: boolean) {
    if ((fromRow && this.clickRow) || !fromRow) {
      this._router.navigate(['/teams/' + teamId]);
      this._localStorageService.setItem('returnUrl', this._router.url);
    }
  }

  initializeDataSourceTeams() {
    this.dataSourceTeams = new MatTableDataSource(this.microsoftTeamsUser);
    this.dataSourceTeams.filterPredicate = this.createTeamsFilter();
  }

  async getMicrosoftUserTeams(userId: string) {
    try {
      this.isBusy = true;
      this.microsoftTeamsUser = await lastValueFrom(
        this._graphSerivce.getMicrosoftUserTeamsInfo(userId)
      );
      this.defaultSortingTeams();
      this.initializeDataSourceTeams();
    } catch (e) {
      let customError: Error = { error: undefined };
      if (e.error) {
        customError = JSON.parse(e.error);
      }
      if (customError.error !== undefined) {
        if (customError.error.code == 'ERR404') {
          this._router.navigate(['/errors/error-404']);
        }
      }
    } finally {
      this.isBusy = false;
    }
  }

  setFilterBasedOnCheckboxes(
    filterToUpdate: FormControl,
    checkbox1: FormControl,
    checkbox2: FormControl,
    getValueFunction: (val: boolean) => unknown
  ) {
    if (checkbox1.value && checkbox2.value) {
      filterToUpdate.setValue('');
    } else if (checkbox1.value) {
      filterToUpdate.setValue(getValueFunction(checkbox1.value));
    } else if (checkbox2.value) {
      filterToUpdate.setValue(getValueFunction(!checkbox2.value));
    } else {
      filterToUpdate.setValue('');
    }
  }

  getIsOwnerValue(isOwner): string {
    return isOwner ? 'Propriétaire' : 'Membre';
  }

  getIsArchivedValue(isArchived): string {
    return isArchived ? 'Archivée' : 'Active';
  }

  createTeamsFilter(): (data: UserTeamGraph, filter: string) => boolean {
    const filterFunction = (data, filter): boolean => {
      const searchTerms = JSON.parse(filter);
      return (
        data.displayName.toLowerCase().indexOf(searchTerms.displayName) !==
          -1 &&
        data.id.toString().toLowerCase().indexOf(searchTerms.id) !== -1 &&
        this.getIsOwnerValue(data.isOwner)
          .toLowerCase()
          .indexOf(searchTerms.userType) !== -1 &&
        this.getIsArchivedValue(data.isArchived)
          .toLowerCase()
          .indexOf(searchTerms.status) !== -1
      );
    };
    return filterFunction;
  }

  defaultSortingTeams() {
    this.microsoftTeamsUser.sort((a, b) => {
      if (a.isArchived === b.isArchived) {
        return 0;
      }
      return a.isArchived ? 1 : -1; // Met `isArchived: false` avant `true`
    });
  }

  checkboxUserTypeChange() {
    this.setFilterBasedOnCheckboxes(
      this.teamsUserTypeFilter,
      this.teamsUserOwnerFilter,
      this.teamsUserMemberFilter,
      val => this.getIsOwnerValue(val)
    );
  }

  checkboxStatusChange() {
    this.setFilterBasedOnCheckboxes(
      this.teamsStatusFilter,
      this.teamsStatusArchivedFilter,
      this.teamsStatusOnlineFilter,
      val => this.getIsArchivedValue(val)
    );
  }

  initTeamsFilter() {
    this.teamsNameFilter.valueChanges.subscribe(name => {
      this.filterValuesTeams.displayName = name.toLowerCase();
      this.dataSourceTeams.filter = JSON.stringify(this.filterValuesTeams);
    });
    this.teamsUserTypeFilter.valueChanges.subscribe(userType => {
      this.filterValuesTeams.userType = userType.toLowerCase();
      this.dataSourceTeams.filter = JSON.stringify(this.filterValuesTeams);
    });
    this.teamsStatusFilter.valueChanges.subscribe(status => {
      this.filterValuesTeams.status = status.toLowerCase();
      this.dataSourceTeams.filter = JSON.stringify(this.filterValuesTeams);
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setVisibleColumns();
  }

  setVisibleColumns() {
    this.clickRow = false;
    this.displayedColumnsTeams = [...this.defaultColumns];
    this.hiddenColumns = [];
    this.expandedUserTeam = null;

    if (window.innerWidth < 850) {
      this.clickRow = true;
      this._toolboxService.hideTableColumnWithoutExpand(
        'view',
        this.displayedColumnsTeams
      );
    }
    if (window.innerWidth < 520) {
      this._toolboxService.hideTableColumn(
        'isArchived',
        this.displayedColumnsTeams,
        this.hiddenColumns
      );
    }
    if (window.innerWidth < 450) {
      this._toolboxService.hideTableColumnWithoutExpand(
        'photo',
        this.displayedColumnsTeams
      );
    }
  }
}
